// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React from "react";
import "./LabelingConflicts.scss";

import { AuthContext } from '../../../context/AuthContext';
import { firestore, storage } from "../../../context/FirebaseConfig";
import { useTranslation } from "react-i18next";

import { collection, query, where, onSnapshot, getCountFromServer } from "firebase/firestore";
import ImageSquare from "../../../shared_components/ImageSquare/ImageSquare";
import ConflictsObservationModal from "./ConflictsObservationModal";

import checkmark_icon from "../../../assets/icons/checkmark.png";
import alert_icon from "../../../assets/icons/alert.png";

function getLabelingConflictsQuery(firestore, clientId) {
    /* Get the query for labeling conflicts */
    const requestsQuery = query(
        collection(firestore, "clients", clientId, "observations"),
        where("labeling_conflict.active", "==", true),
    );
    return requestsQuery;
}

export async function getNumLabelingConflicts(firestore, clientId) {
    /* Get the number of labeling conflicts */
    const labelingConflictsQuery = getLabelingConflictsQuery(firestore, clientId);
    const snapshot = await getCountFromServer(labelingConflictsQuery);
    return snapshot.data().count;
}

export default function LabelingConflicts() {
    const { i18n, t } = useTranslation();
    const { currentUser, labelsets } = React.useContext(AuthContext);
    const [ observations, setObservations ] = React.useState({});
    const [ currentObservationId, setCurrentObservationId ] = React.useState(null);
    const [ scollingObservationIds, setScrollingObservationIds ] = React.useState([]);
    const [ selectedFilter, setSelectedFilter ] = React.useState("");

    React.useEffect(() => {
        const unsubscribe = subscribeToObservations();
        return unsubscribe;
    }, []);

    function subscribeToObservations() {
        /* Subscribe to the observations with labeling conflicts */
        const observationQuery = getLabelingConflictsQuery(firestore, currentUser.company.company);
        const unsubscribe = onSnapshot(observationQuery, snapshot => {
            const newObservations = {};
            snapshot.forEach(doc => {
                newObservations[doc.id] = {...doc.data(), doc_id: doc.id};
            });
            setObservations(newObservations);
        });
        return unsubscribe;
    }

    function getNextObservation(currentObservation) {
        /* Go the next observation. If last loaded observation is reached, load new ones */
        const nextIndex = scollingObservationIds.findIndex(observationId => observationId === currentObservation.doc_id) + 1;
        setCurrentObservationId( scollingObservationIds[Math.min(nextIndex, scollingObservationIds.length-1)] )
    };
    
    function getPreviousObservation (currentObservation) {
        /* Go the previous observation */
        const previousIndex = scollingObservationIds.findIndex(observationId => observationId === currentObservation.doc_id) - 1;
        setCurrentObservationId( scollingObservationIds[Math.max(previousIndex, 0)] )
    };

    function isConflictResolved(observation) {
        /* Check if there is a label for each conflict */
        for (const labelsetId of observation?.labeling_conflict?.labelsets ?? []) {
            if (!observation?.labeling_conflict?.resolution?.[labelsetId]?.label) {
                return false;
            }
        }
        return true;
    }

    function showObservation(observationId, isResolved) {
        /* Show the observation in the modal */
        setCurrentObservationId(observationId);
        setScrollingObservationIds(getFilteredObservationIds(isResolved));
    }

    function getFilteredObservationIds(isResolved) {
        /* Get the filtered observations (correct filter and group) */
        const filteredObservations = Object.keys(observations)
        .filter(observationId => {
            if (selectedFilter && !observations[observationId].labeling_conflict?.labelsets?.includes(selectedFilter)) {
                return false;
            }
            return isConflictResolved(observations[observationId]) === isResolved;
        });
        return filteredObservations;
    }

    return (
        <div className="labeling-conflicts">
            <select className="filter-dropdown" value={selectedFilter} onChange={(e) => {setSelectedFilter(e.target.value)}}>
                <option value="">{t("material_filter_all")}</option>
                { Object.keys(labelsets).map(labelsetId => {
                    return <option key={labelsetId} value={labelsetId}>{labelsets?.[labelsetId]?.name ?? labelsetId}</option>
                }) }
            </select>
            <p className="labeling-conflicts-category">
                <img src={alert_icon} />
                {t("labeling_conflicts_open")}
            </p>
            <div className="labeling-list">
                {getFilteredObservationIds(false)
                .map(observationId => <div 
                    onClick={() => showObservation(observationId, false)}
                    key={observations[observationId].image_url}>
                        <ImageSquare
                            storage={storage}
                            imageUrl={observations[observationId].image_url}
                        />
                </div>)}
            </div>
            <p className="labeling-conflicts-category">
                <img src={checkmark_icon} />
                {t("labeling_conflicts_resolved")}
            </p>
            <div className="labeling-list">
                {getFilteredObservationIds(true)
                .map(observationId => <div 
                    onClick={() => showObservation(observationId, true)}
                    key={observations[observationId].image_url}>
                        <ImageSquare
                            storage={storage}
                            imageUrl={observations[observationId].image_url}
                            icons={[checkmark_icon]}
                        />
                </div>)}
            </div>
            {currentObservationId && <ConflictsObservationModal 
                observation={observations?.[currentObservationId]}
                closeFullscreenModal={() => {setCurrentObservationId(false)}}
                getNext={getNextObservation}
                getPrevious={getPreviousObservation}
            />}
        </div>
    )
}